import { IGroup } from '@fluentui/react/lib/GroupedList';
import { UtteranceCompositeEntityStore } from 'src/luis/projects/testPane/types/types';

const hasChildren = (entity: UtteranceCompositeEntityStore) => entity.children.length !== 0;

/**
 * Generate the entity tree if the entity has children or return one level tree
 * if the entity does not have children.
 * This function generates the n-depth level tree recursively for the given entity
 * and the also the children of that entity.
 *
 * @param entity The entity to generate the tree for.
 * @param expandedKeys Array of the expanded keys items (subtree).
 * @param parentToBeIds Array of entity ids where those entities have to have empty
 * child as a new one.
 * @param level The starting level of depth.
 * @returns A constructed tree with the parent item is the given entity and the children
 * are the sub-trees.
 */
export const generateTestPaneEntityTree = (
    entity: UtteranceCompositeEntityStore,
    expandedKeys: readonly string[],
    isSuppressedModeEnabled: boolean
): IGroup => {
    /**
     * Generates the sub-tree of the children.
     *
     * @param childEntity The child entity.
     * @param subLevel The level of the given entity in the tree.
     * @param parent The parent entity of the child entity.
     */
    const generateChildEntityTree = (
        childEntity: UtteranceCompositeEntityStore,
        subLevel: number,
        parent: UtteranceCompositeEntityStore
    ): IGroup => {
        const childEntityChildren = hasChildren(childEntity)
            ? childEntity.children
                  .filter(c => isSuppressedModeEnabled || !c.parent.isSuppressed)
                  .map<IGroup>(c => generateChildEntityTree(c, subLevel + 1, childEntity))
            : [];
        const groupKey = `${childEntity.parent.utteranceEntity.entity.id}_${childEntity.parent.utteranceEntity.startTokenIndex}_${childEntity.parent.utteranceEntity.endTokenIndex}`;

        return {
            count: 1,
            key: groupKey,
            startIndex: -1,
            level: subLevel,
            children: childEntityChildren,
            data: { entity: childEntity, parent },
            isCollapsed: !expandedKeys.includes(groupKey),
            name: childEntity.parent.utteranceEntity.entity.name
        };
    };

    const key = `${entity.parent.utteranceEntity.entity.id}_${entity.parent.utteranceEntity.startTokenIndex}_${entity.parent.utteranceEntity.endTokenIndex}`;
    const children = hasChildren(entity)
        ? entity.children
              .filter(c => isSuppressedModeEnabled || !c.parent.isSuppressed)
              .map<IGroup>(c => generateChildEntityTree(c, 1, entity))
        : [];

    return {
        key,
        count: 1,
        level: 0,
        children,
        startIndex: -1,
        isCollapsed: !expandedKeys.includes(key),
        name: entity.parent.utteranceEntity.entity.name,
        data: { entity, parent: <UtteranceCompositeEntityStore>null }
    };
};
